import flagsmith from '@flagsmith/flagsmith'
import type {
  FeatureFlagContext,
  FeatureFlagState,
  IFeatureFlagProvider,
} from '../types'

// ÚNICO archivo de la app que importa `flagsmith`.
// Para cambiar de proveedor basta reescribir este adaptador y su factory.

const ENVIRONMENT_ID = import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID
const API_URL = import.meta.env.VITE_FLAGSMITH_API_URL

type FlagsmithTraits = Record<string, string | number | boolean>

/** Convierte el contexto agnóstico en traits del proveedor (sin `userId`). */
function toTraits(context?: FeatureFlagContext): FlagsmithTraits | undefined {
  if (!context) return undefined
  const traits: FlagsmithTraits = {}
  Object.entries(context).forEach(([key, value]) => {
    if (key === 'userId' || value === undefined) return
    traits[key] = value
  })
  return traits
}

export class FlagsmithProvider implements IFeatureFlagProvider {
  private initialized = false
  private listener?: () => void

  subscribe(listener: () => void): void {
    this.listener = listener
  }

  async init(context?: FeatureFlagContext): Promise<void> {
    if (!ENVIRONMENT_ID) {
      console.warn(
        '[FlagsmithProvider] VITE_FLAGSMITH_ENVIRONMENT_ID no configurado; se usarán los valores por defecto.'
      )
      return
    }
    try {
      await flagsmith.init({
        environmentID: ENVIRONMENT_ID,
        cacheFlags: true,
        ...(API_URL ? { api: API_URL } : {}),
        ...(context?.userId ? { identity: context.userId } : {}),
        ...(context ? { traits: toTraits(context) } : {}),
        // Se dispara cuando la evaluación remota llega (incluido el refetch
        // posterior a servir la cache) o cambia la identidad. Re-hidrata el
        // store para que un flag activado tras el snapshot inicial se refleje.
        onChange: () => this.listener?.(),
      })
      this.initialized = true
    } catch (error) {
      console.error(
        '[FlagsmithProvider] init falló; se usarán defaults.',
        error
      )
    }
  }

  async identify(context: FeatureFlagContext): Promise<void> {
    if (!this.initialized || !context.userId) return
    try {
      await flagsmith.identify(context.userId, toTraits(context))
    } catch (error) {
      console.error('[FlagsmithProvider] identify falló.', error)
    }
  }

  async reset(): Promise<void> {
    if (!this.initialized) return
    try {
      await flagsmith.logout()
    } catch (error) {
      console.error('[FlagsmithProvider] reset falló.', error)
    }
  }

  isEnabled(flag: string, fallback = false): boolean {
    try {
      if (!this.initialized) return fallback
      return flagsmith.hasFeature(flag)
    } catch {
      return fallback
    }
  }

  getValue<T = string>(flag: string, fallback?: T): T {
    try {
      if (!this.initialized) return fallback as T
      const value = flagsmith.getValue(flag)
      return (value ?? fallback) as T
    } catch {
      return fallback as T
    }
  }

  getAllFlags(): Record<string, FeatureFlagState> {
    try {
      if (!this.initialized) return {}
      const all = flagsmith.getAllFlags()
      const result: Record<string, FeatureFlagState> = {}
      Object.entries(all).forEach(([key, feature]) => {
        result[key] = {
          enabled: !!feature?.enabled,
          value: feature?.value ?? null,
        }
      })
      return result
    } catch {
      return {}
    }
  }
}
